
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, ArrowRight, Award, HelpCircle } from 'lucide-react';
import { QuizQuestion } from '../types';

interface ResourceQuizProps {
  questions: QuizQuestion[];
  onComplete: (score: number) => void;
  onClose: () => void;
}

const ResourceQuiz: React.FC<ResourceQuizProps> = ({ questions, onComplete, onClose }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [score, setScore] = useState(0);
  const [showResults, setShowResults] = useState(false);

  const currentQuestion = questions[currentQuestionIndex];

  const handleOptionSelect = (index: number) => {
    if (isAnswered) return;
    setSelectedOption(index);
  };

  const handleConfirm = () => {
    if (selectedOption === null) return;
    
    const isCorrect = selectedOption === currentQuestion.correctAnswer;
    if (isCorrect) setScore(prev => prev + 1);
    
    setIsAnswered(true);
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedOption(null);
      setIsAnswered(false);
    } else {
      setShowResults(true);
    }
  };

  if (showResults) {
    const percentage = (score / questions.length) * 100;
    const isPassed = percentage >= 80;

    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white p-12 rounded-[3rem] shadow-2xl border border-slate-100 text-center space-y-8 max-w-2xl mx-auto"
      >
        <div className={`w-24 h-24 mx-auto rounded-3xl flex items-center justify-center ${isPassed ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`}>
          {isPassed ? <Award size={48} /> : <HelpCircle size={48} />}
        </div>
        <div className="space-y-2">
          <h2 className="text-4xl font-serif">{isPassed ? 'Баяр хүргэе!' : 'Дахин оролдоорой'}</h2>
          <p className="text-muted font-medium">
            Та {questions.length} асуултаас {score}-д нь зөв хариуллаа. ({percentage}%)
          </p>
        </div>
        {isPassed ? (
          <p className="text-emerald-600 font-bold uppercase tracking-widest text-xs">
            Та энэ сэдвээр үе тэнгийн багш болох эрхтэй боллоо!
          </p>
        ) : (
          <p className="text-amber-600 font-bold uppercase tracking-widest text-xs">
            Үе тэнгийн багш болохын тулд 80%-иас дээш амжилт үзүүлэх шаардлагатай.
          </p>
        )}
        <div className="flex gap-4">
          <button 
            onClick={() => onComplete(score)}
            className="flex-1 py-5 bg-slate-900 text-white rounded-full font-bold uppercase tracking-widest text-[10px] hover:bg-primary transition-colors"
          >
            Дуусгах
          </button>
          {!isPassed && (
            <button 
              onClick={() => {
                setCurrentQuestionIndex(0);
                setSelectedOption(null);
                setIsAnswered(false);
                setScore(0);
                setShowResults(false);
              }}
              className="flex-1 py-5 border-2 border-slate-100 rounded-full font-bold uppercase tracking-widest text-[10px] hover:bg-slate-50 transition-colors"
            >
              Дахин оролдох
            </button>
          )}
        </div>
      </motion.div>
    );
  }

  return (
    <div className="space-y-8 max-w-3xl mx-auto">
      <div className="flex justify-between items-end">
        <div className="space-y-1">
          <span className="text-[10px] font-bold text-primary uppercase tracking-[0.3em]">Мэдлэг шалгах асуулга</span>
          <h3 className="text-3xl font-serif">Асуулт {currentQuestionIndex + 1}/{questions.length}</h3>
        </div>
        <div className="text-xs font-bold opacity-30 uppercase tracking-widest">
          Зөв хариулт: {score}
        </div>
      </div>

      <div className="bg-white p-10 rounded-[3rem] border border-slate-100 shadow-xl space-y-8">
        <p className="text-xl font-medium leading-relaxed text-slate-800">
          {currentQuestion.question}
        </p>

        <div className="grid grid-cols-1 gap-4">
          {currentQuestion.options.map((option, index) => {
            let statusClass = "bg-slate-50 border-slate-100 hover:border-primary/30";
            if (selectedOption === index) statusClass = "border-primary bg-primary/5 ring-2 ring-primary/20";
            if (isAnswered) {
              if (index === currentQuestion.correctAnswer) statusClass = "border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200";
              else if (selectedOption === index) statusClass = "border-rose-500 bg-rose-50 ring-2 ring-rose-200";
              else statusClass = "opacity-40 border-slate-100 bg-slate-50";
            }

            return (
              <button
                key={index}
                disabled={isAnswered}
                onClick={() => handleOptionSelect(index)}
                className={`w-full p-6 text-left rounded-2xl border-2 transition-all flex items-center justify-between group ${statusClass}`}
              >
                <span className="font-medium">{option}</span>
                {isAnswered && index === currentQuestion.correctAnswer && <CheckCircle2 size={20} className="text-emerald-500" />}
                {isAnswered && selectedOption === index && index !== currentQuestion.correctAnswer && <XCircle size={20} className="text-rose-500" />}
              </button>
            );
          })}
        </div>

        <div className="pt-4">
          {!isAnswered ? (
            <button
              disabled={selectedOption === null}
              onClick={handleConfirm}
              className={`w-full py-5 rounded-full font-bold uppercase tracking-widest text-[10px] transition-all ${
                selectedOption !== null ? 'bg-primary text-white shadow-lg' : 'bg-slate-100 text-slate-400 cursor-not-allowed'
              }`}
            >
              Хариултыг баталгаажуулах
            </button>
          ) : (
            <button
              onClick={handleNext}
              className="w-full py-5 bg-slate-900 text-white rounded-full font-bold uppercase tracking-widest text-[10px] shadow-lg hover:bg-primary transition-all flex items-center justify-center gap-3"
            >
              {currentQuestionIndex < questions.length - 1 ? 'Дараагийн асуулт' : 'Үр дүнг харах'} <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResourceQuiz;
